// Per-session guard for the stale-bundle auto-reload (ENT-1972 / ENT-1981).
//
// After a frontend deploy, an open tab can hold a stale chunk graph. The first
// such chunk error triggers a single cache-busting document refresh (revalidating
// index.html and pulling the current chunks). The refresh is recorded as a
// per-session flag so we never reload-loop: at most one auto-refresh per browser
// session.
//
// To let a long-lived tab auto-recover from a *later* deploy, the guard must be
// released once the app is demonstrably healthy again. ENT-1981 replaces the
// original time-stability heuristic (clear after 60s without an error) with an
// **event-based** clear: the guard is released only when a dynamic import
// actually *resolves* (see `notifyChunkLoadSuccess`, wired into `lazyWithRetry`).
//
// Why event-based is strictly safer: the timer could fire while a route-level
// dynamic import was still pending and clear the guard moments before a very
// slow import finally rejected (>60s CDN stall), which let `componentDidCatch`
// treat the late rejection as a fresh incident and reload once more. A resolve
// signal can never arrive while an import is still in flight, so that race is
// gone — the guard is only cleared by proof that the current bundle loads.

export const CHUNK_RELOAD_KEY = 'sg:chunkReloadAttempted';

const getSessionStorage = (): Storage | undefined => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : undefined;
  } catch {
    return undefined; // storage blocked (e.g. hard privacy mode)
  }
};

/** True if an auto-reload has already been recorded this session. */
export const hasChunkReloadGuard = (): boolean => {
  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }
  try {
    return storage.getItem(CHUNK_RELOAD_KEY) !== null;
  } catch {
    return false;
  }
};

/** Number of auto-reload attempts recorded for this browser session. */
export const getChunkReloadAttemptCount = (): number => {
  const storage = getSessionStorage();
  if (!storage) {
    return 0;
  }
  try {
    const value = storage.getItem(CHUNK_RELOAD_KEY);
    if (value === null) {
      return 0;
    }
    const attempts = Number.parseInt(value, 10);
    return Number.isFinite(attempts) && attempts > 0 ? attempts : 1;
  } catch {
    return 0;
  }
};

/**
 * Record that an auto-reload has fired this session. Returns false if the
 * attempt could not be recorded (storage unavailable), so callers can decline
 * to reload rather than risk an untracked loop.
 */
export const setChunkReloadGuard = (attempts = 1): boolean => {
  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }
  try {
    storage.setItem(CHUNK_RELOAD_KEY, String(Math.max(1, attempts)));
    return true;
  } catch {
    return false;
  }
};

/** Release the per-session reload guard. */
export const clearChunkReloadGuard = (): void => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    /* storage blocked — nothing to clear */
  }
};

/**
 * Signal that a dynamic import resolved successfully. Only clears the guard when
 * it is currently set — i.e. we are in post-reload recovery and have now proven
 * the new bundle graph loads — so a future deploy can re-arm the one-shot
 * reload. In the common (guard-unset) path this is a cheap no-op and never
 * touches storage on every navigation. Event-based by construction: it can only
 * run once an import has actually resolved, never while one is still pending.
 */
export const notifyChunkLoadSuccess = (): void => {
  if (hasChunkReloadGuard()) {
    clearChunkReloadGuard();
  }
};
